import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Link } from 'react-router-dom';
import { 
  Plus, Search, Filter, Ticket, MoreVertical, User, 
  MessageCircle, Mail, Phone, Globe, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Tickets() {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterChannel, setFilterChannel] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicket, setNewTicket] = useState({
    customer_name: '',
    customer_email: '',
    subject: '',
    description: '',
    priority: 'medium',
    channel: 'email',
    category: 'general'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, [token]);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`${API}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Fetch tickets error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.customer_name || !newTicket.subject) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/api/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newTicket)
      });

      if (response.ok) {
        toast.success('Ticket created successfully');
        setShowCreateModal(false);
        setNewTicket({
          customer_name: '',
          customer_email: '',
          subject: '',
          description: '',
          priority: 'medium',
          channel: 'email',
          category: 'general'
        });
        fetchTickets();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Error creating ticket');
    } finally {
      setCreating(false);
    }
  };

  const sampleTickets = [
    { id: 'TKT-1001', customer_name: 'John Smith', customer_email: 'john@example.com', subject: 'Software installation issue', status: 'open', priority: 'high', channel: 'whatsapp', category: 'Technical', agent: 'Sarah M.', updated_at: '2025-05-21T10:30:00Z' },
    { id: 'TKT-1002', customer_name: 'Sarah Johnson', customer_email: 'sarah@example.com', subject: 'AMC renewal query', status: 'in-progress', priority: 'medium', channel: 'email', category: 'AMC/Warranty', agent: 'Mike T.', updated_at: '2025-05-21T09:15:00Z' },
    { id: 'TKT-1003', customer_name: 'Mike Brown', customer_email: 'mike@example.com', subject: 'Billing discrepancy', status: 'resolved', priority: 'low', channel: 'phone', category: 'Billing', agent: 'John D.', updated_at: '2025-05-21T08:00:00Z' },
    { id: 'TKT-1004', customer_name: 'Emily Davis', customer_email: 'emily@example.com', subject: 'Device not responding', status: 'escalated', priority: 'high', channel: 'whatsapp', category: 'Technical', agent: 'Sarah M.', updated_at: '2025-05-20T16:45:00Z' },
    { id: 'TKT-1005', customer_name: 'David Wilson', customer_email: 'david@example.com', subject: 'Password reset request', status: 'resolved', priority: 'low', channel: 'web', category: 'General', agent: 'Auto', updated_at: '2025-05-20T14:30:00Z' },
    { id: 'TKT-1006', customer_name: 'Lisa Anderson', customer_email: 'lisa@example.com', subject: 'Feature request - Dashboard', status: 'open', priority: 'medium', channel: 'email', category: 'General', agent: 'Unassigned', updated_at: '2025-05-20T11:00:00Z' },
  ];

  const displayTickets = tickets.length > 0 ? tickets : sampleTickets;

  const filteredTickets = displayTickets.filter(ticket => {
    const matchesSearch = 
      ticket.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    const matchesChannel = filterChannel === 'all' || ticket.channel === filterChannel;
    return matchesSearch && matchesStatus && matchesPriority && matchesChannel;
  });

  const getStatusClass = (status) => {
    const classes = {
      'open': 'status-open',
      'in-progress': 'status-in-progress',
      'resolved': 'status-resolved',
      'closed': 'status-closed',
      'escalated': 'status-escalated'
    };
    return classes[status] || 'status-open';
  };

  const getPriorityClass = (priority) => {
    const classes = {
      'high': 'priority-high',
      'medium': 'priority-medium',
      'low': 'priority-low'
    };
    return classes[priority] || 'priority-medium';
  };

  const getChannelIcon = (channel) => {
    const icons = {
      'whatsapp': MessageCircle,
      'email': Mail,
      'phone': Phone,
      'web': Globe
    };
    return icons[channel] || Mail;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="w-6 h-6 text-primary" />
            Support Tickets
          </h1>
          <p className="text-muted-foreground">Manage and track all support tickets</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="support-btn-primary flex items-center gap-2"
          data-testid="new-ticket-btn"
        >
          <Plus className="w-4 h-4" />
          <span>New Ticket</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search tickets, customers, subjects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="support-input pl-10"
            data-testid="ticket-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="support-input w-auto"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="escalated">Escalated</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="support-input w-auto"
          >
            <option value="all">All Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="support-input w-auto"
          >
            <option value="all">All Channels</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="web">Web</option>
          </select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="support-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ticket ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Subject</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Priority</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Channel</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Assigned To</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Updated</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket, index) => {
                  const ChannelIcon = getChannelIcon(ticket.channel);
                  return (
                    <tr key={index} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`ticket-row-${ticket.id}`}>
                      <td className="py-3 px-4">
                        <Link to={`/tickets/${ticket.id}`} className="text-primary hover:underline font-medium">
                          {ticket.id}
                        </Link>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-medium">
                            {ticket.customer_name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{ticket.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{ticket.customer_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 max-w-xs truncate">{ticket.subject}</td>
                      <td className="py-3 px-4">
                        <span className={`status-badge ${getStatusClass(ticket.status)}`}>
                          {ticket.status?.replace('-', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`status-badge ${getPriorityClass(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <ChannelIcon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm capitalize">{ticket.channel}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">{ticket.agent || 'Unassigned'}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(ticket.updated_at)}</td>
                      <td className="py-3 px-4">
                        <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
                          <MoreVertical className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredTickets.length === 0 && (
          <div className="text-center py-12">
            <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No tickets found</p>
          </div>
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-lg p-6 animate-fade-in" data-testid="create-ticket-modal">
            <h2 className="text-xl font-bold mb-4">Create New Ticket</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Customer Name *</label>
                  <input
                    type="text"
                    value={newTicket.customer_name}
                    onChange={(e) => setNewTicket({...newTicket, customer_name: e.target.value})}
                    placeholder="Enter customer name"
                    className="support-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Customer Email</label>
                  <input
                    type="email"
                    value={newTicket.customer_email}
                    onChange={(e) => setNewTicket({...newTicket, customer_email: e.target.value})}
                    placeholder="Enter email"
                    className="support-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subject *</label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                  placeholder="Brief description of the issue"
                  className="support-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  placeholder="Detailed description..."
                  rows={4}
                  className="support-input resize-none"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Priority</label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    className="support-input"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Channel</label>
                  <select
                    value={newTicket.channel}
                    onChange={(e) => setNewTicket({...newTicket, channel: e.target.value})}
                    className="support-input"
                  >
                    <option value="email">Email</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="phone">Phone</option>
                    <option value="web">Web</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="support-input"
                  >
                    <option value="general">General</option>
                    <option value="technical">Technical</option>
                    <option value="billing">Billing</option>
                    <option value="amc">AMC/Warranty</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 support-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTicket}
                disabled={creating}
                className="flex-1 support-btn-primary flex items-center justify-center gap-2"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
